/**
 * The payment-method chain, mirrored for the admin UI.
 *
 * `get_room_payment_methods` (20260910160000_payment_methods_by_level.sql) is
 * the authority: the tenant Dashboard, the admin payment hooks and both SEPA
 * charge functions ask it, so nothing can disagree about a room. This module is
 * the same rules in TypeScript, for the two edit sheets — they have to show
 * "Inherit from property (On)" and the owner-collects lock while the admin is
 * still typing, before anything is saved for the resolver to read.
 *
 * Keep the two in step. If the precedence or the clamp changes in the SQL, it
 * changes here in the same commit; payment-methods.test.ts pins the behaviour.
 */

export type PaymentMethodKey = "stripe_card" | "bank_transfer" | "sepa_debit";

/** The column that holds each method's flag at every level. */
export const PAYMENT_METHOD_COLUMNS = {
  stripe_card: "stripe_card_enabled",
  bank_transfer: "bank_transfer_enabled",
  sepa_debit: "sepa_debit_enabled",
} as const satisfies Record<PaymentMethodKey, string>;

export type PaymentMethodColumn =
  (typeof PAYMENT_METHOD_COLUMNS)[PaymentMethodKey];

/**
 * What a room falls back to when no level has an opinion. These are the values
 * the app has always assumed for a room with no configuration row, so they are
 * also what the migration's backfill measured against.
 */
export const PLATFORM_DEFAULT_METHODS: Record<PaymentMethodKey, boolean> = {
  stripe_card: true,
  bank_transfer: false,
  sepa_debit: false,
};

/** Where an effective value came from. Feeds InheritanceBadge. */
export type PaymentMethodSource =
  | "room"
  | "apartment"
  | "property"
  | "platform"
  | "owner_collects";

export type PaymentMethodMeta = {
  key: PaymentMethodKey;
  column: PaymentMethodColumn;
  label: string;
  /** One line under the label — what actually happens to the tenant. */
  description: string;
};

/** Rendered in this order in both sheets: card, transfer, direct debit. */
export const PAYMENT_METHODS: readonly PaymentMethodMeta[] = [
  {
    key: "stripe_card",
    column: "stripe_card_enabled",
    label: "Stripe card",
    description: "Credit or debit card via Stripe Checkout",
  },
  {
    key: "bank_transfer",
    column: "bank_transfer_enabled",
    label: "Bank transfer",
    description: "Tenant transfers manually, then uploads proof",
  },
  {
    key: "sepa_debit",
    column: "sepa_debit_enabled",
    label: "Direct debit (SEPA)",
    description: "We debit the tenant automatically each month",
  },
] as const;

/** Nullable flags as they sit on a room / apartment / property row. */
export type PaymentMethodFlags = {
  stripe_card_enabled?: boolean | null;
  bank_transfer_enabled?: boolean | null;
  sepa_debit_enabled?: boolean | null;
};

export type ResolvedPaymentMethod = {
  enabled: boolean;
  source: PaymentMethodSource;
};

export type ResolvedPaymentMethods = Record<
  PaymentMethodKey,
  ResolvedPaymentMethod
>;

/**
 * Why card and direct debit cannot be used when the owner collects the rent:
 * both rails settle into CoLiviq's Stripe account (there is no Stripe Connect
 * yet), so running them would move the owner's rent into our account. Shown in
 * both sheets next to the locked toggles.
 */
export const OWNER_COLLECTS_LOCK_REASON =
  "The owner collects this rent directly. Card and direct debit settle into coliviq's Stripe account, so they would send the owner's money to us — bank transfer to the owner is the only path until Stripe Connect exists.";

/**
 * Effective methods for a room: room override → apartment → property → platform
 * default, then the owner-collects clamp. A faithful mirror of the SQL resolver.
 */
export function resolvePaymentMethods(args: {
  room?: PaymentMethodFlags | null;
  apartment?: PaymentMethodFlags | null;
  property?: PaymentMethodFlags | null;
  /** Effective collection mode for the room (apartment's, else the property's). */
  collectionMode?: string | null;
}): ResolvedPaymentMethods {
  const ownerCollects = args.collectionMode === "owner_collects";

  const resolveOne = (key: PaymentMethodKey): ResolvedPaymentMethod => {
    const column = PAYMENT_METHOD_COLUMNS[key];
    const levels = [
      ["room", args.room?.[column]],
      ["apartment", args.apartment?.[column]],
      ["property", args.property?.[column]],
    ] as const;

    for (const [source, value] of levels) {
      if (value != null) return { enabled: value, source };
    }
    return { enabled: PLATFORM_DEFAULT_METHODS[key], source: "platform" };
  };

  const resolved: ResolvedPaymentMethods = {
    stripe_card: resolveOne("stripe_card"),
    bank_transfer: resolveOne("bank_transfer"),
    sepa_debit: resolveOne("sepa_debit"),
  };

  if (ownerCollects) {
    return {
      stripe_card: { enabled: false, source: "owner_collects" },
      bank_transfer: { enabled: true, source: "owner_collects" },
      sepa_debit: { enabled: false, source: "owner_collects" },
    };
  }
  return resolved;
}

/**
 * Whether the collection mode decides the methods instead of the admin. Under
 * owner_collects all three are settled — card and direct debit off, transfer on
 * — so every control in the section is read-only, not just the two that turn off.
 */
export function methodsLockedByCollectionMode(
  collectionMode: string | null | undefined
): boolean {
  return collectionMode === "owner_collects";
}

/** "On" / "Off", for inherited-value hints and read-only summaries. */
export function methodStateLabel(enabled: boolean): string {
  return enabled ? "On" : "Off";
}
